import React, { useEffect, useState } from "react";
import { Plus, Trash2, ImagePlus, RefreshCw, ListChecks, UserPlus, Loader2, TrendingUp } from "lucide-react";
import toast from "react-hot-toast";

import ProposalAiButton from "./ProposalAiButton";
import ImageUploadField from "./ImageUploadField";
import VideoEmbedField from "./VideoEmbedField";
import ComparablePickerModal from "./ComparablePickerModal";
import {
  getProposalPropertyPhotos, getProposalMarketStats, getProposalTeammates, getPriceIndication,
} from "../api/prospectingReportsApi";

// A small action button above a section's fields ("Use property photos" etc.).
const ActionButton = ({ icon: Icon, busy, onClick, children }) => (
  <button type="button" onClick={onClick} disabled={busy}
    className="mb-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-[#0f8d9b]/40 bg-[#eef8f9] text-sm font-semibold text-[#0f8d9b] disabled:opacity-60">
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />} {children}
  </button>
);

// Agent cards: pick a CRM staff member and their profile photo, title, phone
// and email come across (CG QA, 26 Oct: "our image is already in the CRM").
const StaffPicker = ({ onPick }) => {
  const [staff, setStaff] = useState([]);
  useEffect(() => {
    getProposalTeammates({ include_self: 1 }).then(({ data }) => setStaff(data?.results || [])).catch(() => {});
  }, []);
  return (
    <div className="mb-3">
      <label className={labelCls}>Add an agent from the CRM</label>
      <div className="flex items-center gap-2">
        <UserPlus className="w-4 h-4 text-[#0f8d9b] shrink-0" />
        <select className={field} value="" onChange={(e) => {
          const s = staff.find((x) => String(x.id) === e.target.value);
          if (s) onPick({ name: s.name, role: s.title || "", photo_url: s.photo_url || "", phone: s.phone || "", email: s.email || "" });
        }}>
          <option value="">Choose a team member…</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.name}{s.title ? ` · ${s.title}` : ""}</option>)}
        </select>
      </div>
    </div>
  );
};

/**
 * Per-section edit form for one proposal block. Fixed, configurable sections —
 * not a free drag-and-drop canvas (Christian, 15 Oct 2026). Calls onChange with
 * the next `data` object.
 */
const field =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40";
const labelCls = "block text-xs font-semibold text-gray-500 mb-1";

const Text = ({ label, value, onChange, area = false, placeholder = "", ai = null }) => (
  <div className="mb-3">
    <div className="flex items-center justify-between mb-1">
      <label className={labelCls} style={{ marginBottom: 0 }}>{label}</label>
      {ai}
    </div>
    {area ? (
      <textarea rows={4} className={field} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className={field} value={value || ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

const ListEditor = ({ label, rows, cols, onChange }) => {
  const update = (i, key, val) => {
    const next = rows.map((r, j) => (j === i ? { ...r, [key]: val } : r));
    onChange(next);
  };
  const add = () => onChange([...(rows || []), Object.fromEntries(cols.map((c) => [c.key, ""]))]);
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div className="mb-3">
      <label className={labelCls}>{label}</label>
      <div className="space-y-2">
        {(rows || []).map((row, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-2 relative">
            {cols.map((c) => (
              c.type === "image" ? (
                <div key={c.key} className="mb-1 pr-6">
                  <ImageUploadField value={row[c.key]} onChange={(url) => update(i, c.key, url)}
                    label={c.label} compact />
                </div>
              ) : (
                <input key={c.key} className={`${field} mb-1`} placeholder={c.label}
                  value={row[c.key] || ""} onChange={(e) => update(i, c.key, e.target.value)} />
              )
            ))}
            <button type="button" onClick={() => remove(i)}
              className="absolute top-2 right-2 text-gray-300 hover:text-red-500" aria-label="Remove">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add}
        className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#0f8d9b]">
        <Plus className="w-4 h-4" /> Add
      </button>
    </div>
  );
};

const ProposalBlockEditor = ({ block, onChange, proposalId, proposal, onApplyEstimate }) => {
  const d = block.data || {};
  const set = (key, value) => onChange({ ...d, [key]: value });
  const [busy, setBusy] = useState(false);
  const [pickComps, setPickComps] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  // Price guide from sold sales (CG QA, 27 Oct): the agent ticks sold comps
  // (Collings + Homer), the range is the middle of those sales (outliers
  // trimmed, editable), and the same sales go into Comparables as evidence.
  const suggestFromSales = async (rows, raw) => {
    const prices = (raw || []).map((c) => Number(c.price)).filter((n) => Number.isFinite(n) && n > 0);
    if (!prices.length) { toast.error("Those sales have no sold price to work from."); return; }
    setBusy(true);
    try {
      const { data } = await getPriceIndication(prices);
      if (!data || data.min == null) { toast.error("Could not work out a range."); return; }
      const next = { ...d, sale_min: String(data.min), sale_max: String(data.max) };
      if (onApplyEstimate) onApplyEstimate(next, rows); else onChange(next);
      setSuggestOpen(false);
      toast.success(`Price guide set from ${data.count} sale${data.count === 1 ? "" : "s"} and added to Comparables.`);
    } catch { toast.error("Could not work out a range."); } finally { setBusy(false); }
  };

  const usePropertyPhotos = async () => {
    setBusy(true);
    try {
      const { data } = await getProposalPropertyPhotos(proposalId);
      const imgs = data?.results || [];
      if (!imgs.length) toast("No photos found on the linked property.");
      else { set("images", imgs); toast.success(`${imgs.length} property photo${imgs.length === 1 ? "" : "s"} added.`); }
    } catch { toast.error("Could not load the property photos."); } finally { setBusy(false); }
  };
  const refreshMarketStats = async () => {
    setBusy(true);
    try {
      const { data } = await getProposalMarketStats(proposalId);
      const items = data?.results || [];
      if (!items.length) toast(`No market data available${data?.suburb ? ` for ${data.suburb}` : ""} yet.`);
      else { set("items", items); toast.success("Market stats updated."); }
    } catch { toast.error("Could not load market data."); } finally { setBusy(false); }
  };
  // AI "Write with AI" / "Refine" button for an AI-writable text field.
  const ai = (field) => (
    <ProposalAiButton
      proposalId={proposalId}
      blockType={block.type}
      field={field}
      currentText={d[field]}
      onText={(t) => set(field, t)}
    />
  );

  switch (block.type) {
    case "cover":
      return (
        <>
          <Text label="Status badge" value={d.badge} onChange={(v) => set("badge", v)}
            placeholder="e.g. OFF MARKET, APPRAISAL" />
          <Text label="Headline" value={d.headline} onChange={(v) => set("headline", v)} />
          <Text label="Subheadline" value={d.subheadline} onChange={(v) => set("subheadline", v)} ai={ai("subheadline")} />
          <Text label="Address (over the hero image)" value={d.address} onChange={(v) => set("address", v)} />
          <ImageUploadField label="Hero image" value={d.image_url} onChange={(v) => set("image_url", v)}
            placeholder="https://… full-width hero photo" />
        </>
      );
    case "about_agent":
      return <Text label="About the agent" area value={d.text} onChange={(v) => set("text", v)} ai={ai("text")} />;
    case "text":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} />
          <Text label="Body" area value={d.body} onChange={(v) => set("body", v)} ai={ai("body")} />
        </>
      );
    case "estimate":
      return (
        <>
          <ActionButton icon={TrendingUp} busy={busy} onClick={() => setSuggestOpen(true)}>Suggest from sold sales</ActionButton>
          {suggestOpen && (
            <ComparablePickerModal proposal={proposal} kinds={["sold"]} onClose={() => setSuggestOpen(false)}
              title="Suggest a price guide from sold sales"
              subtitle="Sold sales from Collings and Homer. Tick the ones that match this property; the range uses the middle of them, and they're added to Comparables."
              actionLabel="Use" onAdd={suggestFromSales} />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Text label="Price from" value={d.sale_min} onChange={(v) => set("sale_min", v)} />
            <Text label="Price to" value={d.sale_max} onChange={(v) => set("sale_max", v)} />
          </div>
          <Text label="Comment" area value={d.comment} onChange={(v) => set("comment", v)} ai={ai("comment")} />
        </>
      );
    case "comparables":
      return (
        <>
          <ActionButton icon={ListChecks} onClick={() => setPickComps(true)}>Pick comparables</ActionButton>
          <Text label="Intro" area value={d.intro} onChange={(v) => set("intro", v)} ai={ai("intro")} />
          <ListEditor label="Comparables on this proposal" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "address", label: "Address" }, { key: "suburb", label: "Suburb" },
                   { key: "beds", label: "Beds" }, { key: "baths", label: "Baths" }, { key: "cars", label: "Cars" },
                   { key: "price_label", label: "Price" }, { key: "sold_date", label: "Sold date" }]} />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!d.use_cma} onChange={(e) => set("use_cma", e.target.checked)} />
            Also show comparable sales from a linked CMA
          </label>
          {pickComps && (
            <ComparablePickerModal proposal={proposal} onClose={() => setPickComps(false)}
              onAdd={(rows) => { set("items", [...(d.items || []), ...rows]); setPickComps(false); toast.success(`${rows.length} comparable${rows.length === 1 ? "" : "s"} added.`); }} />
          )}
        </>
      );
    case "marketing_plan":
      return (
        <>
          <Text label="Intro" area value={d.intro} onChange={(v) => set("intro", v)} ai={ai("intro")} />
          <ListEditor label="Items" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "title", label: "Title" }, { key: "text", label: "Detail" }]} />
        </>
      );
    case "fees":
      return (
        <>
          <Text label="Intro" area value={d.intro} onChange={(v) => set("intro", v)} />
          <Text label="Commission" value={d.commission_text} onChange={(v) => set("commission_text", v)} />
          <ListEditor label="Fee items" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "label", label: "Label" }, { key: "amount_text", label: "Amount" }, { key: "note", label: "Note" }]} />
        </>
      );
    case "testimonials":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} placeholder="What our clients say" />
          <ListEditor label="Testimonials" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "quote", label: "Quote" }, { key: "name", label: "Name" }, { key: "suburb", label: "Suburb" },
                   { key: "rating", label: "Star rating (1-5)" }]} />
        </>
      );
    case "quote":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} placeholder="Marketing investment" />
          <Text label="Intro" area value={d.intro} onChange={(v) => set("intro", v)} ai={ai("intro")} />
          <ListEditor label="Items" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "label", label: "Item (e.g. Professional photography)" }, { key: "amount", label: "Amount (e.g. 850)" }]} />
          <div className="grid grid-cols-2 gap-2">
            <Text label="GST rate %" value={d.gst_rate} onChange={(v) => set("gst_rate", v)} placeholder="10" />
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-5">
              <input type="checkbox" checked={!!d.gst_inclusive} onChange={(e) => set("gst_inclusive", e.target.checked)} />
              Amounts include GST
            </label>
          </div>
          <Text label="Note" value={d.note} onChange={(v) => set("note", v)} placeholder="e.g. Why it works…" />
          <p className="text-[11px] text-gray-400">Subtotal, GST and total are calculated automatically.</p>
        </>
      );
    case "sale_method":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} placeholder="Sale method & pricing" />
          <Text label="Method of sale" value={d.method} onChange={(v) => set("method", v)} placeholder="Auction / Private sale / Expressions of interest" />
          <Text label="Why this method" area value={d.intro} onChange={(v) => set("intro", v)} ai={ai("intro")} />
          <ListEditor label="Reasons" rows={d.reasons} onChange={(v) => set("reasons", v)}
            cols={[{ key: "text", label: "Reason (e.g. Power of competition)" }]} />
          <Text label="Campaign length" value={d.campaign_length} onChange={(v) => set("campaign_length", v)} placeholder="28 days from launch to deadline" />
          <div className="grid grid-cols-2 gap-2">
            <Text label="Advertised range from" value={d.price_min} onChange={(v) => set("price_min", v)} />
            <Text label="Advertised range to" value={d.price_max} onChange={(v) => set("price_max", v)} />
          </div>
          <Text label="Pricing note" value={d.price_note} onChange={(v) => set("price_note", v)} />
        </>
      );
    case "steps":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} placeholder="Next steps" />
          <Text label="Intro" area value={d.intro} onChange={(v) => set("intro", v)} ai={ai("intro")} />
          <ListEditor label="Steps (numbered)" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "title", label: "Step" }, { key: "text", label: "Detail" }]} />
        </>
      );
    case "listing_details":
      return (
        <>
          <Text label="Intro" area value={d.intro} onChange={(v) => set("intro", v)} ai={ai("intro")} />
          <ListEditor label="Details" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "label", label: "Label (e.g. Bedrooms)" }, { key: "value", label: "Value" }]} />
        </>
      );
    case "open_home_times":
      return (
        <>
          <Text label="Intro" area value={d.intro} onChange={(v) => set("intro", v)} ai={ai("intro")} />
          <ListEditor label="Times" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "when", label: "When (e.g. Sat 11:00–11:30am)" }, { key: "note", label: "Note" }]} />
        </>
      );
    case "cta":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} />
          <Text label="Text" area value={d.text} onChange={(v) => set("text", v)} ai={ai("text")} />
          <Text label="Accept button label" value={d.accept_label} onChange={(v) => set("accept_label", v)} />
          <label className="flex items-center gap-2 text-sm text-gray-700 mb-1">
            <input type="checkbox" checked={!!d.show_accept} onChange={(e) => set("show_accept", e.target.checked)} />
            Show an Accept button
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={!!d.show_callback} onChange={(e) => set("show_callback", e.target.checked)} />
            Show a Request callback button
          </label>
        </>
      );
    case "gallery":
      return (
        <>
          <ActionButton icon={ImagePlus} busy={busy} onClick={usePropertyPhotos}>Use property photos</ActionButton>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} />
          <ListEditor label="Photos" rows={d.images} onChange={(v) => set("images", v)}
            cols={[{ key: "image_url", label: "Photo", type: "image" }, { key: "caption", label: "Caption" }]} />
        </>
      );
    case "feature_list":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} />
          <Text label="Intro" area value={d.intro} onChange={(v) => set("intro", v)} ai={ai("intro")} />
          <ListEditor label="Features" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "text", label: "Feature" }]} />
        </>
      );
    case "metrics":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} />
          <ListEditor label="Figures" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "label", label: "Label (e.g. Weekly rent)" }, { key: "value", label: "Value (e.g. $1,000)" }]} />
          <Text label="Footnote" value={d.footnote} onChange={(v) => set("footnote", v)} />
          <label className="flex items-center gap-2 text-sm text-gray-700 mt-1">
            <input type="checkbox" checked={!!d.dark} onChange={(e) => set("dark", e.target.checked)} />
            Show on a dark band
          </label>
        </>
      );
    case "market_stats":
      return (
        <>
          <ActionButton icon={RefreshCw} busy={busy} onClick={refreshMarketStats}>Refresh from market data</ActionButton>
          <p className="text-[11px] text-gray-400 -mt-2 mb-3">From Collings sales and Homer market data for the suburb. Figures we don't have stay hidden.</p>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} />
          <Text label="Intro" area value={d.intro} onChange={(v) => set("intro", v)} ai={ai("intro")} />
          <ListEditor label="Statistics" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "label", label: "Label" }, { key: "value", label: "Value" },
                   { key: "trend", label: "Trend (up/down)" }]} />
        </>
      );
    case "map":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} />
          <Text label="Address" value={d.address} onChange={(v) => set("address", v)} />
          <div className="grid grid-cols-2 gap-2">
            <Text label="Latitude" value={d.lat} onChange={(v) => set("lat", v)} placeholder="-37.81" />
            <Text label="Longitude" value={d.lng} onChange={(v) => set("lng", v)} placeholder="144.96" />
          </div>
          <p className="text-xs text-gray-400">With lat/long an interactive map embeds; otherwise a “View on map” link shows.</p>
        </>
      );
    case "agents":
      return (
        <>
          <StaffPicker onPick={(row) => {
            const rows = d.items || [];
            if (rows.some((r) => (r.email || "").toLowerCase() === row.email.toLowerCase() && row.email)) {
              set("items", rows.map((r) => ((r.email || "").toLowerCase() === row.email.toLowerCase() ? { ...r, ...row } : r)));
            } else set("items", [...rows, row]);
          }} />
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} />
          <ListEditor label="Agents" rows={d.items} onChange={(v) => set("items", v)}
            cols={[{ key: "name", label: "Name" }, { key: "role", label: "Role" },
                   { key: "photo_url", label: "Photo (optional, CRM profile photo is used)", type: "image" },
                   { key: "phone", label: "Phone" }, { key: "email", label: "Email" }]} />
        </>
      );
    case "embed":
      return (
        <>
          <Text label="Heading" value={d.heading} onChange={(v) => set("heading", v)} />
          <VideoEmbedField value={d.embed_url} onChange={(v) => set("embed_url", v)} />
          <ImageUploadField label="Thumbnail (for uploaded / MP4 video)" value={d.poster_url}
            onChange={(v) => set("poster_url", v)} placeholder="Shown before the video plays" />
          <Text label="Caption" area value={d.caption} onChange={(v) => set("caption", v)} />
        </>
      );
    default:
      return <p className="text-sm text-gray-400">No editor for this section.</p>;
  }
};

export default ProposalBlockEditor;
